/**
 * AdminProfessionalDetail — /admin/marketplace/professionals/:id
 *
 * Wave 5-Late Marketplace propagation. Executive discipline inherited
 * from anchor:
 *   • AdminLayout wrap; back-to-list link consumes ink-muted token.
 *   • Section primitive with hairline chrome across Identity, Contact,
 *     Flags, Verification, Danger Zone.
 *   • Inputs / textarea on `--ds-radius-input` (3px) +
 *     `--ds-color-input-border`, `px-2.5 py-1.5`.
 *   • Category-toggle chips use `--ds-radius-input` (sharp, not pill).
 *   • Verification pill via DS Badge with sharp shape + executive tone
 *     mapping (same as AdminProfessionalsTab). `statusBadge()` from
 *     verificationFsm is imported for its `.label` metadata only — the
 *     helper file itself is UNCHANGED (logic freeze per user directive).
 *   • Save + Delete + Verification action buttons use the shared
 *     GhostButton / AccentButton primitives; destructive variant swaps
 *     to danger-soft / danger tokens.
 *   • `ConfirmDialog` (v2 shell) retained for two-flow confirm modals.
 *
 * Business logic (dirty-patch compute, FSM transitions, 409 delete
 * blocked → deactivate flow, guardrail 422 codes) preserved BYTE-EXACT.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Contact2,
  Flag,
  Save,
  ShieldCheck,
  Trash2,
  UserSquare2,
} from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "@/admin/AdminLayout";
import ConfirmDialog from "@/design-system/components/ConfirmDialog";
import { api, formatApiError } from "@/api/client";
import { actionsForStatus, statusBadge } from "./verificationFsm";
import { Badge } from "@/design-system/components/Badge";
import { AccentButton, GhostButton, Section } from "@/admin/_primitives";

const CONTACT_PREFS = [
  { value: "platform_only", label: "Platform only" },
  { value: "email_intro", label: "Email intro" },
  { value: "phone_visible", label: "Phone visible" },
];
const RATE_RANGES = ["$", "$$", "$$$", "$$$$"];

const EDITABLE_KEYS = [
  "name",
  "categories",
  "bio",
  "service_area",
  "years_experience",
  "website_url",
  "contact_email",
  "public_contact_preference",
  "phone_number",
  "rate_range",
  "verification_proof_url",
  "is_demo",
  "is_active",
];

// Verification tone mapping (same as anchor). `statusBadge()` from
// verificationFsm.js is still imported for its `.label` string, but
// its rgba color output is bypassed on this surface.
const VERIFICATION_TONE = {
  pending: "warning",
  approved: "neutralMuted",
  active: "successMuted",
  rejected: "danger",
};

function computeDirtyPatch(baseline, form) {
  const patch = {};
  for (const k of EDITABLE_KEYS) {
    const a = baseline?.[k];
    const b = form?.[k];
    if (JSON.stringify(a) !== JSON.stringify(b)) patch[k] = b;
  }
  return patch;
}

/* ------------------------------------------------------------------ */
/* Local admin primitives.                                              */
/* ------------------------------------------------------------------ */


function FieldLabel({ children }) {
  return (
    <span
      className="text-[10.5px] uppercase tracking-[0.16em] font-semibold block mb-1"
      style={{ color: "var(--ds-color-ink-subtle)" }}
    >
      {children}
    </span>
  );
}

const inputCls =
  "w-full rounded-[var(--ds-radius-input)] border bg-[var(--ds-color-surface)] px-2.5 py-1.5 text-[13px] text-[var(--ds-color-ink)] focus:outline-none focus:border-[var(--ds-color-input-border-strong)]";

const inputStyle = { borderColor: "var(--ds-color-input-border)" };



function DangerButton({ children, className = "", ...rest }) {
  return (
    <button
      type="button"
      {...rest}
      className={
        "inline-flex items-center gap-1.5 rounded-[var(--ds-radius-field)] border px-3 py-1.5 " +
        "text-[12px] font-semibold uppercase tracking-[0.08em] disabled:opacity-60 transition-colors " +
        className
      }
      style={{
        borderColor: "var(--ds-color-danger)",
        color: "var(--ds-color-danger)",
        background: "var(--ds-color-danger-soft)",
        transitionDuration: "var(--ds-duration-fast)",
        ...rest.style,
      }}
    >
      {children}
    </button>
  );
}

/* ------------------------------------------------------------------ */
/* Root                                                                 */
/* ------------------------------------------------------------------ */

export default function AdminProfessionalDetail() {
  const { professionalId } = useParams();
  const navigate = useNavigate();
  const [pro, setPro] = useState(null);
  const [form, setForm] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [verificationBusy, setVerificationBusy] = useState(false);

  const [pendingAction, setPendingAction] = useState(null);
  const [notesDraft, setNotesDraft] = useState("");
  const [notesError, setNotesError] = useState(null);

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteBlocked, setDeleteBlocked] = useState(null);

  const load = useCallback(async () => {
    if (!professionalId) return;
    setLoading(true);
    setError(null);
    try {
      const [proRes, catRes] = await Promise.all([
        api.get("/admin/marketplace/professionals", { params: { limit: 500 } }),
        api.get("/admin/marketplace/categories"),
      ]);
      const p = (proRes.data || []).find((r) => r.id === professionalId);
      if (!p) {
        setError("Professional not found.");
        setPro(null);
        setForm(null);
      } else {
        setPro(p);
        setForm({ ...p });
      }
      setCategories(catRes.data || []);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [professionalId]);

  useEffect(() => {
    load();
  }, [load]);

  const dirtyPatch = useMemo(
    () => (pro && form ? computeDirtyPatch(pro, form) : {}),
    [pro, form],
  );
  const isDirty = Object.keys(dirtyPatch).length > 0;

  async function save() {
    if (!isDirty) return;
    setSaving(true);
    try {
      const { data } = await api.patch(
        `/admin/marketplace/professionals/${professionalId}`,
        dirtyPatch,
      );
      setPro(data);
      setForm({ ...data });
      toast.success("Profile saved.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const code = typeof detail === "object" ? detail?.code : null;
      if (code === "professional_content_failed_guardrail") {
        toast.error("Name contains disallowed language — please rephrase.");
      } else if (code === "bio_failed_guardrail") {
        toast.error("Bio contains disallowed language — please rephrase.");
      } else if (code === "unknown_category") {
        toast.error(`Unknown categories: ${(detail?.unknown_slugs || []).join(", ")}`);
      } else {
        toast.error(formatApiError(err));
      }
    } finally {
      setSaving(false);
    }
  }

  function startAction(action) {
    setPendingAction(action);
    setNotesDraft("");
    setNotesError(null);
  }

  async function confirmVerificationAction() {
    if (!pendingAction) return;
    if (pendingAction.requires_notes && !notesDraft.trim()) {
      setNotesError("Notes are required to reject this professional.");
      return;
    }
    setVerificationBusy(true);
    try {
      const body = { status: pendingAction.next };
      if (notesDraft.trim()) body.verification_notes = notesDraft.trim();
      const { data } = await api.patch(
        `/admin/marketplace/professionals/${professionalId}/verification`,
        body,
      );
      setPro(data);
      setForm({ ...data });
      toast.success(`Verification updated → ${data.verification_status}.`);
      setPendingAction(null);
      setNotesDraft("");
      setNotesError(null);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const code = typeof detail === "object" ? detail?.code : null;
      if (code === "verification_notes_failed_guardrail") {
        setNotesError("Notes contain disallowed language — please rephrase.");
      } else if (code === "invalid_verification_transition") {
        toast.error(
          `Illegal transition: ${detail?.current} → ${detail?.requested}. Reload and try again.`,
        );
        setPendingAction(null);
      } else {
        toast.error(formatApiError(err));
      }
    } finally {
      setVerificationBusy(false);
    }
  }

  async function doDelete() {
    setDeleteBusy(true);
    setDeleteBlocked(null);
    try {
      await api.delete(`/admin/marketplace/professionals/${professionalId}`);
      toast.success("Professional deleted.");
      navigate("/admin/marketplace?tab=professionals");
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const code = typeof detail === "object" ? detail?.code : null;
      if (status === 409 && code === "professional_has_consultation_references") {
        setDeleteBlocked({ reference_count: detail?.reference_count || 0 });
      } else {
        toast.error(formatApiError(err));
      }
    } finally {
      setDeleteBusy(false);
    }
  }

  async function deactivateInstead() {
    setDeleteBusy(true);
    try {
      const { data } = await api.patch(
        `/admin/marketplace/professionals/${professionalId}`,
        { is_active: false },
      );
      setPro(data);
      setForm({ ...data });
      setDeleteBlocked(null);
      setDeleteConfirmOpen(false);
      toast.success("Professional deactivated. Public listing will hide it.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDeleteBusy(false);
    }
  }

  const activeCategories = useMemo(
    () =>
      (categories || []).filter(
        (c) => c.is_active || (form?.categories || []).includes(c.slug),
      ),
    [categories, form],
  );

  return (
    <AdminLayout
      title={pro?.name || "Professional detail"}
      subtitle={loading ? "Loading…" : pro?.contact_email || null}
      actions={
        <Link
          to="/admin/marketplace?tab=professionals"
          data-testid="admin-pro-detail-back"
          className="inline-flex items-center gap-1.5 text-[12px] uppercase tracking-[0.14em]"
          style={{ color: "var(--ds-color-ink-muted)" }}
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to list
        </Link>
      }
    >
      <div data-testid="admin-pro-detail-root">
        {loading && (
          <div className="text-[13px]" style={{ color: "var(--ds-color-ink-muted)" }}>
            Loading…
          </div>
        )}
        {error && !loading && (
          <div
            className="rounded-[var(--ds-radius-field)] p-3 text-[13px]"
            style={{
              background: "var(--ds-color-danger-soft)",
              border: "1px solid var(--ds-color-danger)",
              color: "var(--ds-color-danger)",
            }}
          >
            {error}
          </div>
        )}

        {form && !loading && !error && (
          <div className="grid gap-3 lg:grid-cols-[1fr_320px]">
            <div>
              <Section title="Identity" icon={UserSquare2} testid="admin-pro-detail-identity">
                <div className="space-y-3">
                  <div>
                    <FieldLabel>Name</FieldLabel>
                    <input
                      data-testid="admin-pro-detail-name"
                      className={inputCls}
                      style={inputStyle}
                      value={form.name}
                      onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    />
                  </div>
                  <div>
                    <FieldLabel>Bio</FieldLabel>
                    <textarea
                      data-testid="admin-pro-detail-bio"
                      className={inputCls}
                      style={{ ...inputStyle, minHeight: 100 }}
                      maxLength={500}
                      value={form.bio}
                      onChange={(e) => setForm((f) => ({ ...f, bio: e.target.value }))}
                    />
                  </div>
                  <div>
                    <FieldLabel>Categories</FieldLabel>
                    <div className="flex flex-wrap gap-1.5">
                      {activeCategories.map((cat) => {
                        const on = form.categories.includes(cat.slug);
                        return (
                          <button
                            key={cat.slug}
                            type="button"
                            data-testid={`admin-pro-detail-cat-${cat.slug}`}
                            onClick={() =>
                              setForm((f) => ({
                                ...f,
                                categories: on
                                  ? f.categories.filter((s) => s !== cat.slug)
                                  : [...f.categories, cat.slug],
                              }))
                            }
                            className="text-[11px] uppercase tracking-[0.08em] px-2 py-1 rounded-[var(--ds-radius-input)] border transition-colors"
                            style={{
                              background: on
                                ? "var(--ds-color-accent-soft)"
                                : "var(--ds-color-surface)",
                              color: on
                                ? "var(--ds-color-accent-strong)"
                                : "var(--ds-color-ink-muted)",
                              borderColor: on
                                ? "var(--ds-color-accent)"
                                : "var(--ds-color-admin-border)",
                              transitionDuration: "var(--ds-duration-fast)",
                            }}
                          >
                            {cat.display_name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </Section>

              <Section title="Contact & rates" icon={Contact2} testid="admin-pro-detail-contact">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <FieldLabel>Contact email</FieldLabel>
                    <input
                      data-testid="admin-pro-detail-email"
                      type="email"
                      className={inputCls}
                      style={inputStyle}
                      value={form.contact_email || ""}
                      onChange={(e) => setForm((f) => ({ ...f, contact_email: e.target.value }))}
                    />
                  </div>
                  <div>
                    <FieldLabel>Website</FieldLabel>
                    <input
                      data-testid="admin-pro-detail-website"
                      className={inputCls}
                      style={inputStyle}
                      value={form.website_url || ""}
                      onChange={(e) => setForm((f) => ({ ...f, website_url: e.target.value }))}
                    />
                  </div>
                  <div>
                    <FieldLabel>Contact preference</FieldLabel>
                    <select
                      data-testid="admin-pro-detail-contact-pref"
                      className={inputCls}
                      style={inputStyle}
                      value={form.public_contact_preference}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, public_contact_preference: e.target.value }))
                      }
                    >
                      {CONTACT_PREFS.map((c) => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <FieldLabel>Rate range</FieldLabel>
                    <select
                      data-testid="admin-pro-detail-rate"
                      className={inputCls}
                      style={inputStyle}
                      value={form.rate_range}
                      onChange={(e) => setForm((f) => ({ ...f, rate_range: e.target.value }))}
                    >
                      {RATE_RANGES.map((r) => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <FieldLabel>Phone number</FieldLabel>
                    <input
                      data-testid="admin-pro-detail-phone"
                      className={inputCls}
                      style={inputStyle}
                      value={form.phone_number || ""}
                      onChange={(e) => setForm((f) => ({ ...f, phone_number: e.target.value }))}
                    />
                  </div>
                  <div>
                    <FieldLabel>Years experience</FieldLabel>
                    <input
                      data-testid="admin-pro-detail-years"
                      type="number"
                      min={0}
                      max={70}
                      className={inputCls + " tabular-nums"}
                      style={inputStyle}
                      value={form.years_experience || 0}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          years_experience: Number(e.target.value) || 0,
                        }))
                      }
                    />
                  </div>
                </div>
              </Section>

              <Section title="Flags" icon={Flag} testid="admin-pro-detail-flags">
                <div className="space-y-2 text-[13px]">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      data-testid="admin-pro-detail-is-active"
                      checked={form.is_active}
                      onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
                      className="accent-[var(--ds-color-accent)]"
                    />
                    <span style={{ color: "var(--ds-color-ink)" }}>
                      Active — visible in public marketplace when verification_status is "active"
                    </span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      data-testid="admin-pro-detail-is-demo"
                      checked={form.is_demo}
                      onChange={(e) => setForm((f) => ({ ...f, is_demo: e.target.checked }))}
                      className="accent-[var(--ds-color-accent)]"
                    />
                    <span style={{ color: "var(--ds-color-ink)" }}>
                      Demo profile — blocks consultation-requests; shows Demo Profile badge
                    </span>
                  </label>
                </div>
              </Section>

              <div className="flex items-center gap-3">
                <AccentButton
                  data-testid="admin-pro-detail-save"
                  disabled={!isDirty || saving}
                  onClick={save}
                >
                  <Save className="w-3.5 h-3.5" />
                  {saving ? "Saving…" : "Save profile edits"}
                </AccentButton>
                {isDirty && (
                  <span
                    data-testid="admin-pro-detail-dirty"
                    className="text-[11px] uppercase tracking-[0.14em] tabular-nums"
                    style={{ color: "var(--ds-color-ink-muted)" }}
                  >
                    {Object.keys(dirtyPatch).length} unsaved change
                    {Object.keys(dirtyPatch).length !== 1 ? "s" : ""}
                  </span>
                )}
              </div>
            </div>

            <div>
              <VerificationPanel pro={pro} onAction={startAction} />

              <Section title="Danger zone" icon={Trash2} testid="admin-pro-danger">
                <DangerButton
                  onClick={() => setDeleteConfirmOpen(true)}
                  data-testid="admin-pro-delete-btn"
                >
                  <Trash2 className="w-3 h-3" /> Delete professional
                </DangerButton>
                <p className="mt-2 text-[11px]" style={{ color: "var(--ds-color-ink-muted)" }}>
                  Deletion is blocked if any consultation requests reference this pro. Use
                  "Deactivate instead" in that case.
                </p>
              </Section>
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={Boolean(pendingAction)}
        onCancel={() => {
          if (!verificationBusy) {
            setPendingAction(null);
            setNotesDraft("");
            setNotesError(null);
          }
        }}
        onConfirm={confirmVerificationAction}
        title={pendingAction ? `Confirm: ${pendingAction.label}` : ""}
        description={
          <div className="space-y-3">
            <div>
              {pendingAction && `Move ${pro?.name || "this professional"} to `}
              <strong>{pendingAction?.next}</strong>?
            </div>
            {pendingAction?.requires_notes && (
              <div>
                <FieldLabel>Reason (required)</FieldLabel>
                <textarea
                  data-testid="admin-verification-notes"
                  className={inputCls}
                  style={{
                    ...inputStyle,
                    borderColor: notesError
                      ? "var(--ds-color-danger)"
                      : "var(--ds-color-input-border)",
                    minHeight: 70,
                  }}
                  value={notesDraft}
                  onChange={(e) => {
                    setNotesDraft(e.target.value);
                    setNotesError(null);
                  }}
                  placeholder="Explain why (visible to admin only)."
                />
                {notesError && (
                  <div
                    data-testid="admin-verification-notes-error"
                    className="text-[11px] mt-1"
                    style={{ color: "var(--ds-color-danger)" }}
                  >
                    {notesError}
                  </div>
                )}
              </div>
            )}
            {!pendingAction?.requires_notes && (
              <div>
                <FieldLabel>Notes (optional)</FieldLabel>
                <textarea
                  data-testid="admin-verification-notes"
                  className={inputCls}
                  style={{ ...inputStyle, minHeight: 60 }}
                  value={notesDraft}
                  onChange={(e) => setNotesDraft(e.target.value)}
                  placeholder="Optional context for the audit trail."
                />
              </div>
            )}
          </div>
        }
        confirmLabel={pendingAction?.label || "Confirm"}
        variant={pendingAction?.variant === "destructive" ? "destructive" : "default"}
        busy={verificationBusy}
        testId="admin-verification-confirm"
      />

      <ConfirmDialog
        open={deleteConfirmOpen}
        onCancel={() => {
          if (!deleteBusy) {
            setDeleteConfirmOpen(false);
            setDeleteBlocked(null);
          }
        }}
        onConfirm={deleteBlocked ? deactivateInstead : doDelete}
        title={deleteBlocked ? "Cannot delete — deactivate instead?" : "Delete professional?"}
        description={
          deleteBlocked ? (
            <div className="space-y-2" data-testid="admin-pro-delete-blocked-hint">
              <p>
                Cannot delete — this professional has{" "}
                <strong>{deleteBlocked.reference_count}</strong> consultation request
                {deleteBlocked.reference_count === 1 ? "" : "s"} referencing them. Consider
                deactivating instead: the pro will disappear from the public marketplace but
                historical leads stay intact.
              </p>
            </div>
          ) : (
            <p>This permanently deletes the professional profile. This cannot be undone.</p>
          )
        }
        confirmLabel={deleteBlocked ? "Deactivate instead" : "Delete permanently"}
        variant={deleteBlocked ? "default" : "destructive"}
        busy={deleteBusy}
        testId="admin-pro-delete-confirm"
      />
    </AdminLayout>
  );
}

function VerificationPanel({ pro, onAction }) {
  const meta = statusBadge(pro.verification_status);
  const actions = actionsForStatus(pro.verification_status);
  const tone = VERIFICATION_TONE[pro.verification_status] || "neutralMuted";
  return (
    <Section title="Verification" icon={ShieldCheck} testid="admin-verification-panel">
      <div className="space-y-3">
        <div>
          <Badge
            tone={tone}
            size="md"
            shape="sharp"
            data-testid="admin-verification-badge"
            className="uppercase tracking-[0.14em]"
          >
            {pro.verification_status}
          </Badge>
          <div className="mt-1 text-[11px]" style={{ color: "var(--ds-color-ink-muted)" }}>
            {meta.label}
          </div>
        </div>

        {pro.verification_notes && (
          <div
            data-testid="admin-verification-notes-display"
            className="rounded-[var(--ds-radius-input)] p-2.5 text-[12px]"
            style={{
              background: "var(--ds-color-admin-surface-muted)",
              border: "1px solid var(--ds-color-admin-border)",
              color: "var(--ds-color-ink)",
            }}
          >
            <div
              className="text-[10.5px] uppercase tracking-[0.16em] font-semibold mb-1"
              style={{ color: "var(--ds-color-ink-subtle)" }}
            >
              Notes
            </div>
            {pro.verification_notes}
          </div>
        )}

        <div className="flex flex-col gap-2">
          {actions.length === 0 && (
            <div className="text-[12px]" style={{ color: "var(--ds-color-ink-muted)" }}>
              No verification transitions available from this state.
            </div>
          )}
          {actions.map((a) => {
            if (a.variant === "destructive") {
              return (
                <DangerButton
                  key={a.key}
                  data-testid={a.test_id}
                  onClick={() => onAction(a)}
                  className="w-full justify-center"
                >
                  {a.label}
                </DangerButton>
              );
            }
            if (a.variant === "primary") {
              return (
                <AccentButton
                  key={a.key}
                  data-testid={a.test_id}
                  onClick={() => onAction(a)}
                  className="w-full justify-center"
                >
                  {a.label}
                </AccentButton>
              );
            }
            return (
              <GhostButton
                key={a.key}
                data-testid={a.test_id}
                onClick={() => onAction(a)}
                className="w-full justify-center"
              >
                {a.label}
              </GhostButton>
            );
          })}
        </div>
      </div>
    </Section>
  );
}
